import React, { useEffect, useState } from "react";
import { ApplicantRoutes, RecruiterRoutes, CommonRoutes, ClientRoutes } from "./routes";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import RouteLoading from "../components/CommonComponents/RouteLoading";
import {
  getCompanyAgencyOnboardingPath,
  getUserRole,
  getEffectiveRouteSetKey,
  isCandidateApplicationPath,
  isPendingUserRoleSelection,
  requiresCompanyAgencyOnboarding,
  ROUTE_SET_KEYS,
  ROLE_IDS,
} from "../constants/roleConfig";

const RoleBasedRoutes = () => {
  const userRole = useSelector(
    (state) => getUserRole(state.auth.user) ?? state.auth.role
  );
  const jobSeekerId = useSelector(
    (state) => state.auth.user?.job_seeker_id ?? state.auth.jobseeker_id
  );
  const profileCompleted = useSelector(
    (state) => state?.auth?.user?.profile_completed
  );
  const groups = useSelector((state) => state?.auth?.user?.groups);
  const companyId = useSelector((state) => state?.auth?.user?.company_id);
  // Get token from Redux state (stored in sessionStorage via redux-persist)
  const token = useSelector((state) => state.auth.token);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [previousRole, setPreviousRole] = useState(null);
  const isUserRolePath =
    location.pathname === "/user-role" ||
    location.pathname.startsWith("/user-role/");
  const hasPendingOwnerRoleSync = (() => {
    if (typeof window === "undefined") return false;
    try {
      const pendingRole = sessionStorage.getItem("pending_owner_role_sync");
      return pendingRole === String(ROLE_IDS.RECRUITER) || pendingRole === String(ROLE_IDS.AGENCY);
    } catch {
      return false;
    }
  })();

  // Do not call handleInvalidToken() from !token && userRole here: on logout,
  // RESET_APP / persist rehydration can briefly leave role without token (or the
  // opposite), which falsely showed "Session Expired" after navigating home.
  // Expired sessions are handled by the axios 401 interceptor in api/instance.js.

  // Track loading state and previous role
  useEffect(() => {
    if (userRole !== undefined && userRole !== null) {
      setPreviousRole(userRole);
      setIsLoading(false);
    } else if (!token) {
      setIsLoading(false);
    }
  }, [userRole, token]);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    if (isLoading && token) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 1000); // Wait max 1 second for role to load

      return () => clearTimeout(timeout);
    }
  }, [isLoading, token]);

  // Incomplete role 3 (User): never mount recruiter/applicant/client route trees until
  // /user-role is done (typing /dashboard bypasses CheckRoute).
  // Same exception as PrivateRoute: a candidate mid-application is API role 3
  // with no jobseeker id until the resume upload completes, so this guard would
  // otherwise pull them out of the flow and into the account-type picker. This
  // gate runs BEFORE PrivateRoute, so it needs the exception too - fixing only
  // one of the two leaves the bug exactly as it was.

  if (
    token &&
    isPendingUserRoleSelection(userRole, jobSeekerId) &&
    !isCandidateApplicationPath(location.pathname) &&
    location.pathname !== "/user-role" &&
    !location.pathname.startsWith("/user-role/")
  ) {
    return <Navigate to="/user-role" replace />;
  }

  if (
    token &&
    requiresCompanyAgencyOnboarding(userRole, profileCompleted, groups, companyId) &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/try-haire/interview") &&
    !(isUserRolePath && hasPendingOwnerRoleSync)
  ) {
    return (
      <Navigate to={getCompanyAgencyOnboardingPath(userRole)} replace />
    );
  }

  // User-role selection page exists only in CommonRoutes. When user is on /user-role
  // (e.g. new user after OTP), always render CommonRoutes so the page is found.
  if (isUserRolePath) {
    return <CommonRoutes />;
  }

  // Onboarding (company/agency details) is in CommonRoutes; allow role 1 and 5 to complete it.
  if (location.pathname.startsWith('/onboarding')) {
    return <CommonRoutes />;
  }

  // Email deep links (API-style path) live in CommonRoutes for all roles — same gate as public login redirect.
  if (location.pathname.startsWith('/myrequirements/')) {
    return <CommonRoutes />;
  }

  // Client dashboard path: allow access when on client routes (role 4)
  if (location.pathname.startsWith('/client-dashboard')) {
    return <ClientRoutes />;
  }

  // If user has a token but role is not yet loaded, preserve route structure
  // This prevents 404 errors during Redux state rehydration
  // Check if current path suggests recruiter routes
  const isRecruiterPath = location.pathname.startsWith('/dashboard') || 
                          location.pathname.startsWith('/view-jobs-data') ||
                          location.pathname.startsWith('/try-haire/interview') ||
                          location.pathname.startsWith('/check-out') ||
                          location.pathname.startsWith('/congratulations') ||
                          location.pathname.startsWith('/better-luck') ||
                          location.pathname.startsWith('/payment-') ||
                          location.pathname.startsWith('/view-invited-jobs-data') ||
                          location.pathname.startsWith('/view-requested-jobs-data') ||
                          location.pathname.startsWith('/view-demo-jobs-data') ||
                          location.pathname.startsWith('/meeting') ||
                          location.pathname.startsWith('/credit-success') ||
                          location.pathname.startsWith('/contact-sales') ||
                          location.pathname.startsWith('/interview-analyser') ||
                          location.pathname.startsWith('/inteview-reshedule') ||
                          location.pathname.startsWith('/company-details') ||
                          location.pathname.startsWith('/confirm-information') ||
                          location.pathname.includes('/view-jobs-data/results') ||
                          location.pathname.includes('/view-jobs-data');

  const isClientPath = location.pathname.startsWith('/client-dashboard');

  const isApplicantPath = location.pathname.startsWith('/applicant-dashboard') ||
                          location.pathname.startsWith('/resume-') ||
                          location.pathname.startsWith('/jobs') ||
                          location.pathname.startsWith('/applicantprofile') ||
                          location.pathname.startsWith('/help/troubleshoot-microphone') ||
                          location.pathname.startsWith('/ai-practice-interview') ||
                          location.pathname.startsWith('/written-assessment') ||
                          location.pathname.startsWith('/code-editor') ||
                          location.pathname.startsWith('/update-applicant-resume') ||
                          location.pathname.includes('/under-review') ||
                          (location.pathname.startsWith('/confirm-information') && token) ||
                          (location.pathname.startsWith('/account-verification') && token) ||
                          (location.pathname.startsWith('/interview-analyser') && token) ||
                          (location.pathname.startsWith('/inteview-reshedule') && token) ||
                          (location.pathname.startsWith('/check-out') && token) ||
                          (location.pathname.startsWith('/congratulations') && token) ||
                          (location.pathname.startsWith('/better-luck') && token);

  const isPublicPath = location.pathname === '/' ||
                       location.pathname.startsWith('/home') ||
                       location.pathname.startsWith('/about-us') ||
                       location.pathname.startsWith('/contact-us') ||
                       location.pathname.startsWith('/features') ||
                       location.pathname.startsWith('/pricing') ||
                       location.pathname.startsWith('/Pricing') ||
                       location.pathname.startsWith('/privacy-policy') ||
                       location.pathname.startsWith('/PrivacyPolicy') ||
                       location.pathname.startsWith('/terms-and-conditions') ||
                       location.pathname.startsWith('/Term&Condition') ||
                       location.pathname.startsWith('/cookie-policy') ||
                       location.pathname.startsWith('/refund-policy') ||
                       location.pathname.startsWith('/RefundPolicy') ||
                       location.pathname.startsWith('/login') ||
                       location.pathname.startsWith('/sign-up') ||
                       location.pathname.startsWith('/user-role') ||
                       location.pathname.startsWith('/forget-password') ||
                       location.pathname.startsWith('/reset-password') ||
                       location.pathname.startsWith('/otp-verification') ||
                       location.pathname.startsWith('/new-login-with-verification-code') ||
                       location.pathname.startsWith('/recruiting-job-seeking') ||
                       location.pathname.startsWith('/try-haire') ||
                       location.pathname.startsWith('/ai-interview') ||
                       location.pathname.startsWith('/analyser') ||
                       location.pathname.startsWith('/recruiter') ||
                       location.pathname.startsWith('/pagenotfound') ||
                       location.pathname.startsWith('/maintainance-page') ||
                       location.pathname.startsWith('/link-expired');
  
  // Check for company profile pages (format: /:companyName/:companyId)
  const isCompanyProfilePath = /^\/[^\/]+\/[^\/]+$/.test(location.pathname) && 
                               !location.pathname.includes('dashboard') &&
                               !location.pathname.includes('applicant-dashboard') &&
                               !isRecruiterPath && 
                               !isApplicantPath && 
                               !isPublicPath;

  // During initial load or state rehydration
  if (token && (userRole === null || userRole === undefined)) {
    // If user is on /user-role page, always show CommonRoutes to allow access
    if (location.pathname === '/user-role' || location.pathname.startsWith('/user-role')) {
      return <CommonRoutes />;
    }
    
    // Use previous role if available (structured config supports role 3 & 4 for client)
    const routeSetKey = getEffectiveRouteSetKey(
      previousRole,
      jobSeekerId,
      location.pathname
    );
    if (routeSetKey === ROUTE_SET_KEYS.RECRUITER) return <RecruiterRoutes />;
    if (routeSetKey === ROUTE_SET_KEYS.APPLICANT) return <ApplicantRoutes />;
    if (routeSetKey === ROUTE_SET_KEYS.CLIENT) return <ClientRoutes />;
    if (routeSetKey === ROUTE_SET_KEYS.AGENCY) return <RecruiterRoutes />;

    // If path suggests client routes, show client routes
    if (isClientPath) {
      return <ClientRoutes />;
    }
    // If path suggests recruiter routes, show recruiter routes
    if (isRecruiterPath) {
      return <RecruiterRoutes />;
    }
    // If path suggests applicant routes, show applicant routes
    if (isApplicantPath) {
      return <ApplicantRoutes />;
    }
    // If it's a known public path or company profile path, show common routes
    if (isPublicPath || isCompanyProfilePath) {
      return <CommonRoutes />;
    }
    
    // Default behavior: For logged-in users with unknown paths,
    // wait a bit more for role to load to prevent 404
    if (isLoading) {
      return <RouteLoading />; // Show loading indicator
    }
    
    // For logged-in users, try to intelligently determine the route type
    // based on common patterns to avoid 404 errors
    if (location.pathname.includes('dashboard') || 
        location.pathname.includes('job') || 
        location.pathname.includes('applicant') ||
        location.pathname.includes('resume') ||
        location.pathname.includes('interview') ||
        location.pathname.includes('assessment')) {
      // These are likely protected routes, default to role-based routing
      // Use applicant routes as default for job-related paths
      return <ApplicantRoutes />;
    }
    
    // If still no role after loading, default to common routes
    return <CommonRoutes />;
  }

  // Handle case where token exists but role is 0 or falsy (but not null/undefined)
  if (token && !userRole && userRole !== null && userRole !== undefined) {
    // User is logged in but has no role assigned yet
    return <CommonRoutes />;
  }

  // Normal role-based routing (config supports role 4 = client, 3 = legacy client, 5 = agency).
  // getEffectiveRouteSetKey, not getRouteSetKeyForRole: role 3 (User) otherwise maps to the
  // recruiter tree, which has no /resume-builder or job routes, so a candidate arriving from
  // the lead-form OTP flow (role 3, no jobseeker id yet) rendered PageNotFound. It also covers
  // role 3 WITH a jobseeker id - the case the unreachable check below used to aim at.
  const routeSetKey = getEffectiveRouteSetKey(
    userRole,
    jobSeekerId,
    location.pathname
  );
  if (routeSetKey === ROUTE_SET_KEYS.RECRUITER) return <RecruiterRoutes />;
  if (routeSetKey === ROUTE_SET_KEYS.APPLICANT) return <ApplicantRoutes />;
  if (routeSetKey === ROUTE_SET_KEYS.CLIENT) return <ClientRoutes />;
  if (routeSetKey === ROUTE_SET_KEYS.AGENCY) return <RecruiterRoutes />;
  return <CommonRoutes />;
};

export default RoleBasedRoutes;
